import { useCallback, useRef } from 'react';
import { useMatch } from 'react-router-dom';
import { useAppSettingsStore } from '@/shared/lib';
import type { Language } from '@/shared/types';
import { useRegistrationStore } from '@/entities/registration';
import { useRegistrationLanguageSync } from './useRegistrationLanguageSync';

export function useSignupLanguageSwitch() {
  const { getLanguage, setLanguage } = useAppSettingsStore();
  const { getActiveRegistrationState } = useRegistrationStore();
  const { syncRegistrationLanguage } = useRegistrationLanguageSync();
  const signupMatch = useMatch('/:locale?/signup/*');
  const registrationMatch = useMatch('/:locale?/Registration/*');
  const isSignupRoute = !!signupMatch || !!registrationMatch;

  const openedLanguageRef = useRef<Language | null>(null);
  const pendingLanguageRef = useRef<Language | null>(null);

  const changeLanguage = useCallback(
    (language: Language) => {
      setLanguage(language);
      pendingLanguageRef.current = language;
    },
    [setLanguage]
  );

  const flushLanguage = useCallback(() => {
    const openedLanguage = openedLanguageRef.current;
    openedLanguageRef.current = null;

    setTimeout(() => {
      const language = pendingLanguageRef.current;
      pendingLanguageRef.current = null;

      const state = getActiveRegistrationState();
      if (
        !language ||
        language === openedLanguage ||
        !isSignupRoute ||
        !state
      ) {
        return;
      }

      syncRegistrationLanguage(state, language);
    }, 0);
  }, [getActiveRegistrationState, isSignupRoute, syncRegistrationLanguage]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        openedLanguageRef.current = getLanguage();
        return;
      }
      flushLanguage();
    },
    [getLanguage, flushLanguage]
  );

  return {
    language: getLanguage(),
    changeLanguage,
    onOpenChange,
  };
}
